// Demo mode lets the product/business team show the app when the database has
// no data for the current season. It is OFF unless REACT_APP_DEMO_MODE=true.
//
// Each feature can use one of three data sources:
//   "live"   - normal backend data (same as without demo mode)
//   "replay" - real backend data from REACT_APP_DEMO_REPLAY_DATE, moved forward
//              so it looks like this week's data
//   "mock"   - generated dummy data around today's date (no backend data needed)
//
// Writes (POST/PUT/DELETE) are never sent to the backend in demo mode.

export const FEATURE_MODES = ["live", "replay", "mock"];

// Defaults used when demo mode is on and REACT_APP_DEMO_FEATURES does not override them.
const DEFAULT_FEATURE_MODES = {
  report: "mock",
  indoorClimate: "mock",
  dataVisualization: "mock",
};

// Which backend endpoints belong to which feature.
const FEATURE_ROUTES = [
  { feature: "report", kind: "harvestEvents", pattern: /^\/cultivation-zone\/(\d+)\/harvest-events$/ },
  { feature: "report", kind: "prediction", pattern: /^\/cultivation-zone\/(\d+)\/prediction$/ },
  { feature: "report", kind: "offlinePrediction", pattern: /^\/cultivation-zone\/(\d+)\/offline-yield-prediction$/ },
  { feature: "indoorClimate", kind: "climateData", pattern: /^\/cultivation-zone\/(\d+)\/climate-data$/ },
  { feature: "dataVisualization", kind: "parametersList", pattern: /^\/data-visualization\/cultivation-zone\/(\d+)\/parameters-list$/ },
  { feature: "dataVisualization", kind: "parametersValue", pattern: /^\/data-visualization\/cultivation-zone\/(\d+)\/parameters-value$/ },
];

export const isDemoMode = () => process.env.REACT_APP_DEMO_MODE === "true";

export const getReplayDate = () => process.env.REACT_APP_DEMO_REPLAY_DATE || null;

// REACT_APP_DEMO_FEATURES format: "report:replay,indoorClimate:mock"
const parseFeatureOverrides = (value) => {
  if (!value) return {};
  return value.split(",").reduce((acc, pair) => {
    const [feature, mode] = pair.split(":").map((part) => part && part.trim());
    if (feature && FEATURE_MODES.includes(mode)) {
      acc[feature] = mode;
    } else if (pair.trim()) {
      console.warn(`[demo] Ignoring invalid REACT_APP_DEMO_FEATURES entry "${pair}"`);
    }
    return acc;
  }, {});
};

export const getFeatureMode = (feature) => {
  const modes = {
    ...DEFAULT_FEATURE_MODES,
    ...parseFeatureOverrides(process.env.REACT_APP_DEMO_FEATURES),
  };
  const mode = modes[feature] || "live";
  if (mode === "replay" && !getReplayDate()) {
    console.warn(`[demo] "${feature}" is set to replay but REACT_APP_DEMO_REPLAY_DATE is empty, using mock data`);
    return "mock";
  }
  return mode;
};

// Returns { feature, kind, cropId } for demo-aware endpoints, otherwise null.
export const matchDemoRoute = (pathname) => {
  for (const route of FEATURE_ROUTES) {
    const match = pathname.match(route.pattern);
    if (match) {
      return { feature: route.feature, kind: route.kind, cropId: Number(match[1]) };
    }
  }
  return null;
};
